import useCricketScore from '../../hooks/useCricketScore'

/* The sidebar cricket scorecard, socket-driven (useCricketScore, which hands
 * back the whole `summary.data` frame).
 *
 * Reference (admin bundle, the `1 == gameDetail.scard && "0" == isfinished`
 * branch — the one the user's capture shows):
 *
 *   <div class="scorecard">
 *     <div class="scorecard-row"><div class="score-top-row">
 *       <div class="score-team"><b>{{spnnation1}}</b> {{score1}}</div>
 *       <div class="score-rr">
 *         <span v-if="spnrunrate1 !== ''">CRR {{spnrunrate1}}</span>
 *         <span v-if="spnreqrate1 !== ''">RR  {{spnreqrate1}}</span>
 *       </div>
 *       <div class="score-message" v-if="spnballrunningstatus != ''">
 *         <span v-if="dayno !== ''">Day {{dayno}} |</span> {{spnballrunningstatus}}</div>
 *       <div class="score-message" v-if="spnmessage != ''">
 *         <span v-if="dayno !== ''">Day {{dayno}} |</span> {{spnmessage}}</div>
 *     </div></div>
 *     <div class="scorecard-row"><div class="score-top-row">
 *       <div class="score-team"><b>{{spnnation2}}</b> {{score2}}</div>
 *       <div class="score-rr">
 *         <span v-if="spnrunrate2 !== ''">CRR {{spnrunrate2}}</span>
 *         <span v-if="spnreqrate2 !== ''">RR  {{spnreqrate2}}</span>
 *       </div>
 *       <div class="score-message"><span class="ball-runs mr-1" …>…</span></div>
 *     </div></div>
 *   </div>
 *
 * We used to render the two team rows, ONE run rate (whichever of the two was
 * non-empty, always on the first row) and the balls — the required rate, the
 * chase message and the ball-running status were simply dropped. They are all
 * in the frame; checked live on gmid 462855645 (Japan v India, `scard: 1`):
 *
 *   spnnation1 "JAP"  score1 "2-1 (0.5)"  spnrunrate1 "2.40"  spnreqrate1 "7.44"
 *   spnnation2 "IND"  score2 "32-4 (5.0)" spnrunrate2 ""      spnreqrate2 ""
 *   spnmessage "JAP Needed 31 runs from 25 balls"   spnballrunningstatus ""
 *   dayno ""   isfinished "0"   balls ["1","0","0","0","1ww",""]
 *
 * so each row carries its OWN pair of rates — which is why the reference reads
 * `…1` on the first row and `…2` on the second instead of picking one.
 */
// `virtual`: the T5/T10 XI page's copy — its rates div is a bare <div> (no
// `.score-rr`), so CRR sits on its own line under the score.
export default function CricketScorecard({ gmid, started = true, virtual = false }) {
  const data = useCricketScore(gmid, { enabled: !!gmid && started })
  if (!data) return null

  const {
    spnnation1, spnnation2, score1, score2,
    spnrunrate1, spnrunrate2, spnreqrate1, spnreqrate2,
    spnmessage, spnballrunningstatus, dayno, isfinished, balls,
  } = data
  // the reference renders this card only while the match is live
  if (String(isfinished ?? '0') !== '0') return null

  const ballList = Array.isArray(balls) ? balls : []
  const txt = (v) => String(v ?? '')
  // "Day 2 |" only on a multi-day match
  const dayPrefix = txt(dayno) !== ''
    ? <span>{'\n                    Day\n                    ' + txt(dayno) + ' |\n                  '}</span>
    : null

  const rates = (crr, rr) => (
    <div className={virtual ? undefined : 'score-rr'}>
      {txt(crr) !== '' ? <span>{'\n                    CRR\n                    ' + txt(crr) + '\n                  '}</span> : null}
      {txt(crr) !== '' && txt(rr) !== '' ? ' ' : null}
      {txt(rr) !== '' ? <span>{'\n                    RR\n                    ' + txt(rr) + '\n                  '}</span> : null}
    </div>
  )

  const message = (body) => (
    <div className="score-message">
      {dayPrefix}
      {'\n                  ' + body + '\n                '}
    </div>
  )

  const teamRow = (nat, score, crr, rr, extra) => (
    <div className="scorecard-row">
      <div className="score-top-row">
        <div className="score-team">
          {/* no space inside <b> — the reference is `<b>JAP</b>` and the gap
              comes from the score's own leading newline */}
          <b>{nat}</b>
          {'\n                  ' + (score || '') + '\n                '}
        </div>
        {rates(crr, rr)}
        {extra}
      </div>
    </div>
  )

  const ballStrip = ballList.length > 0 ? (
    <div className="score-message">
      {/* 4 → yellow, 6 → green, `ww` → red (.ball-runs.four/.six/.wicket,
          style.css:3828/3832/3836); an empty ball is skipped, a wide like
          "1w" stays plain — reference:
          `:class="[{four: 4 == s}, {six: 6 == s}, {wicket: 'ww' == s.toLowerCase()}]"` */}
      {ballList.map((b, i) => {
        const v = String(b ?? '')
        if (v === '') return null
        const mod = Number(v) === 4 ? ' four'
          : Number(v) === 6 ? ' six'
            : v.toLowerCase() === 'ww' ? ' wicket' : ''
        return <span key={`ball-${i}`} className={`ball-runs mr-1${mod}`}>{b}</span>
      })}
    </div>
  ) : null

  return (
    <div className="scorecard">
      {teamRow(spnnation1, score1, spnrunrate1, spnreqrate1, (
        <>
          {txt(spnballrunningstatus) !== '' ? message(txt(spnballrunningstatus)) : null}
          {txt(spnmessage) !== '' ? message(txt(spnmessage)) : null}
        </>
      ))}
      {teamRow(spnnation2, score2, spnrunrate2, spnreqrate2, ballStrip)}
    </div>
  )
}
